import React from 'react';
import { HOME_THEME as T } from '../../lib/juniorTheme';
import { useJuniorAliveNav } from '../../contexts/JuniorAliveNavContext';

interface ActivityShellProps {
  title: string;
  subtitle: string;
  backTo: string;
  backLabel: string;
  immersive?: boolean;
  /** Modo presentación (TV/proyector): sin chrome, contenido a pantalla completa. */
  presentation?: boolean;
  /** Evita scroll en el área de contenido (wizard, juegos). */
  lockViewport?: boolean;
  /** Oculta la barra con botón atrás en modo immersive. */
  hideImmersiveHeader?: boolean;
  /** Sin padding lateral en el contenido (juegos a pantalla completa). */
  fullBleed?: boolean;
  children: React.ReactNode;
}

export const ActivityShell: React.FC<ActivityShellProps> = ({
  title,
  subtitle,
  backTo,
  backLabel,
  immersive = false,
  presentation = false,
  lockViewport = false,
  hideImmersiveHeader = false,
  fullBleed = false,
  children,
}) => {
  const { navigateAlive } = useJuniorAliveNav();
  const hideHeader = hideImmersiveHeader || presentation;
  const bleed = fullBleed || presentation;

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 ${
        immersive ? 'h-full max-h-full overflow-hidden' : 'min-h-[calc(100dvh-80px)]'
      }`}
      style={{ background: immersive ? undefined : T.bgGradient }}
    >
      {!immersive && (
        <div className="shrink-0 px-4 sm:px-8 pt-4 pb-2">
          <button
            type="button"
            onClick={() => navigateAlive(backTo)}
            className="inline-flex items-center gap-2 text-sm font-bold mb-2 hover:opacity-70"
            style={{ color: T.muted }}
          >
            ← {backLabel}
          </button>
          <h1 className="junior-display text-3xl sm:text-4xl" style={{ color: T.text }}>
            {title}
          </h1>
          <p className="text-sm font-semibold mt-1" style={{ color: T.muted }}>
            {subtitle}
          </p>
        </div>
      )}

      {immersive && !hideHeader && (
        <div className="ar-immersive-header shrink-0 flex items-center gap-2 px-2 py-1">
          <button
            type="button"
            onClick={() => navigateAlive(backTo)}
            className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-lg"
            aria-label={`Volver a ${backLabel}`}
          >
            ←
          </button>
          <div className="flex-1 text-center min-w-0">
            <p className="ar-immersive-title junior-display text-base sm:text-lg leading-tight truncate">
              {title}
            </p>
          </div>
          <div className="w-10 shrink-0" aria-hidden />
        </div>
      )}

      <div
        className={`flex flex-col flex-1 min-h-0 ${
          lockViewport || presentation ? 'overflow-hidden' : ''
        } ${presentation ? 'activity-shell--presentation' : ''} ${
          bleed ? 'px-0 pb-0' : immersive ? 'px-1 sm:px-2 pb-1' : 'px-4 sm:px-8 pb-8'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default ActivityShell;
